import { useEffect, useRef, useState } from "react";

/**
 * Live MDCE stock chart powered by TradingView (free embed, no API key).
 * Symbol: OTC:MDCE — Medical Care Technologies Inc. on OTC Pink.
 *
 * The TradingView widget ships a ~500KB script that pulls its full charting
 * bundle on demand. Since this section is below the fold, we defer mounting
 * the widget until the section is about to enter the viewport — this shaves
 * ~1s off initial page load on slow connections.
 */
export default function StockChart() {
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // Only fetch/mount the TradingView bundle once the section is close to view.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShouldLoad(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "300px 0px" } // start loading a bit before it's actually in view
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return undefined;
    const host = containerRef.current;
    if (!host) return undefined;
    // Clear any previous instance (StrictMode double-mount safety)
    host.innerHTML = "";

    const widget = document.createElement("div");
    widget.className = "tradingview-widget-container__widget";
    widget.style.height = "600px";
    widget.style.width = "100%";

    const credit = document.createElement("div");
    credit.className = "tradingview-widget-copyright";
    credit.style.fontSize = "11px";
    credit.style.padding = "6px 8px";
    credit.style.color = "rgba(180, 160, 210, 0.7)";
    credit.innerHTML =
      '<a href="https://www.tradingview.com/symbols/OTC-MDCE/" rel="noopener nofollow" target="_blank" style="color:#d6b8ff;text-decoration:none;">MDCE chart</a> by TradingView';

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.innerHTML = JSON.stringify({
      width: "100%",
      height: 600,
      symbol: "OTC:MDCE",
      interval: "D",
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      toolbar_bg: "#0b0612",
      enable_publishing: false,
      hide_top_toolbar: false,
      hide_legend: false,
      withdateranges: true,
      allow_symbol_change: false,
      save_image: false,
      backgroundColor: "rgba(11, 6, 18, 1)",
      gridColor: "rgba(160, 107, 255, 0.08)",
      studies: ["MASimple@tv-basicstudies"],
      support_host: "https://www.tradingview.com",
    });

    host.appendChild(widget);
    host.appendChild(credit);
    host.appendChild(script);

    return () => {
      host.innerHTML = "";
    };
  }, [shouldLoad]);

  return (
    <section id="stock" ref={sectionRef} data-testid="stock-section" className="relative py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
          <div>
            <div className="section-num">06 / LIVE MARKET</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold mt-3 leading-[1.05]">
              <span className="text-white">OTC PINK · </span>
              <span className="text-gradient-violet">MDCE</span>
            </h2>
            <p className="mt-3 text-[#cfc4e6] max-w-xl">
              Real-time stock activity for Medical Care Technologies Inc.
            </p>
          </div>
          <div className="tag-pill" data-testid="stock-status">
            <span className="dot" /> LIVE FEED · TRADINGVIEW
          </div>
        </div>

        <div className="glass-panel p-2 lg:p-3">
          <div
            className="rounded-xl overflow-hidden bg-[#0b0612] tradingview-widget-container"
            ref={containerRef}
            style={{ width: "100%" }}
            data-testid="tradingview-widget"
          />
        </div>

        <p className="mt-4 text-[11px] text-[#8d83a6] font-mono-tech tracking-wider">
          MARKET DATA PROVIDED BY TRADINGVIEW · DELAYS MAY APPLY
        </p>
      </div>
    </section>
  );
}
